import React, { useState, useMemo } from 'react';
import { ReportInfoPanel } from '../../../shared/components/ReportInfoPanel';
import { useQuery } from '@apollo/client';
import {
  APPRAISAL_SPONSOR,
  APPRAISAL_TEAM_LEAD
} from '../../../../constants/appraisal-team-member-types';
import _ from 'lodash';
import ApolloError from '../../../shared/components/ApolloError';
import GET_ORG_UNITS_DETAILED_REPORT from '../../queries/getOrgUnitDetailedReportQuery';
import CharacterizationLegend from '../CharacterizationLegend';
import UnitDetailsBox from '../../../shared/components/UnitDetailsBox';
import GET_FDA_DETAILED_APPRAISAL_REPORT from '../../queries/getFdaDetailedAppraisalReportQuery';
import ProgramYearFilter from '../../../shared/components/ProgramYearFilter';
import { isUserFDATier } from '../../../shared/services/checkUserTierService';
import PageInstructions from '../../../shared/components/PageInstructions';
import GET_ORG_UNITS_SHARED_REPORTS_WITH_FDA from '../../queries/getOrgUnitSharedReportsWithFdaQuery';
import NotEnoughDataMessage from '../../../shared/components/NotEnoughDataMessage';
import { toHumanReadableDate } from '../../../shared/services/dateService';
import { DetailedReportTable } from './DetailedReportTable';
import { useLocation, useNavigate } from 'react-router-dom';
import { SelectedPracticeInformation } from '@shared/components/SelectedPracticeInformation';
import { pageUrlService } from '@shared/services/pageUrlService';

/**
 * @typedef {object} DetailedAppraisalReportProps
 * @property {number} orgUnitId
 * @property {string} [tier]
 */

/**
 * @param {DetailedAppraisalReportProps} props
 */
const DetailedAppraisalReport = props => {
  const { orgUnitId, tier } = props;

  const location = useLocation();
  const orgUnitSelectedAppraisalId = location.state
    ? location.state.orgUnitSelectedAppraisalId
    : null;

  const [selectedAppraisalId, setSelectedAppraisalId] = useState(orgUnitSelectedAppraisalId);
  const [selectedPracticeData, setSelectedPracticeData] = useState(null);

  const { data, error, loading } = useQuery(
    isUserFDATier(tier) ? GET_FDA_DETAILED_APPRAISAL_REPORT : GET_ORG_UNITS_DETAILED_REPORT,
    {
      variables: { id: orgUnitId }
    }
  );

  const {
    data: sharedUnitsWithFda,
    error: sharedUnitsWithFdaError,
    loading: sharedUnitsWithFdaLoading
  } = useQuery(GET_ORG_UNITS_SHARED_REPORTS_WITH_FDA, {
    variables: { orgUnitId: orgUnitId },
    skip: isUserFDATier(tier)
  });

  const navigate = useNavigate();

  const getPageData = () => {
    if (!data) {
      return {
        orgUnit: null,
        appraisalOrgUnits: null,
        selectedAppraisalOrgUnit: null,
        programYearOptions: null,
        isPercentageVisible: null,
        practiceAreas: null
      };
    }

    const orgUnit = !isUserFDATier(tier)
      ? data.detailedOrgUnitReport
      : data.fdaDetailedAppraisalReport;

    const appraisalOrgUnits = orgUnit.AppraisalOrgUnits;

    if (!appraisalOrgUnits.some(ou => ou.Appraisal.Id === selectedAppraisalId)) {
      const appraisalOrgUnitMaxProgramYear = _.maxBy(
        appraisalOrgUnits,
        unit => unit.AppraisalProgramYear
      );
      setSelectedAppraisalId(appraisalOrgUnitMaxProgramYear.Appraisal.Id);
    }

    const selectedAppraisalOrgUnit = selectedAppraisalId
      ? appraisalOrgUnits.find(a => a.Appraisal.Id === selectedAppraisalId)
      : appraisalOrgUnits[appraisalOrgUnits.length - 1];

    const programYearOptions = appraisalOrgUnits
      .map(ou => ({
        value: ou.AppraisalProgramYear,
        key: ou.Appraisal.Id
      }))
      .reverse();

    return {
      orgUnit,
      appraisalOrgUnits,
      selectedAppraisalOrgUnit,
      programYearOptions,
      isPercentageVisible: data.isPercentageVisible,
      practiceAreas: data.practiceAreas
    };
  };

  const {
    orgUnit,
    appraisalOrgUnits,
    selectedAppraisalOrgUnit,
    programYearOptions,
    isPercentageVisible,
    practiceAreas
  } = getPageData();

  const isProgramYearSharedWithFDA = useMemo(() => {
    if (!selectedAppraisalOrgUnit) {
      return false;
    }

    if (
      sharedUnitsWithFdaLoading ||
      !sharedUnitsWithFda ||
      !sharedUnitsWithFda.mddapOrgUnitFdaSharing
    ) {
      return false;
    }

    return sharedUnitsWithFda.mddapOrgUnitFdaSharing.SharedDetailedAppraisalReports.some(
      sharedDetailedAppraisalReport =>
        sharedDetailedAppraisalReport.AppraisalId === selectedAppraisalOrgUnit.Appraisal.Id ||
        sharedDetailedAppraisalReport.AppraisalId === null
    );
  }, [sharedUnitsWithFdaLoading, sharedUnitsWithFda, selectedAppraisalOrgUnit]);

  const sharedWithFdaDisclaimerFlag =
    !sharedUnitsWithFdaLoading && isProgramYearSharedWithFDA && !isUserFDATier(tier);

  const description =
    'Appraisal Results displayed for a selected Program Year, including Practice Areas (PAs) ' +
    'appraised, the resulting practice characterizations within a PA, and the PA aggregate percentages ' +
    '(the average result of all practice characterizations within a PA). ' +
    'Select Program Year or Click on a Practice Characterization to see more info.';

  const handleSelectUnit = orgUnitId => {
    setSelectedPracticeData(null);
    navigate(pageUrlService.getDetailedAppraisalReportPageUrl(orgUnitId), {
      state: {
        orgUnitSelectedAppraisalId: selectedAppraisalId
      }
    });
  };

  if (error || sharedUnitsWithFdaError) {
    return <ApolloError />;
  }

  const renderAppraisalInfoPanelContent = () => {
    const appraisal = selectedAppraisalOrgUnit.Appraisal;
    const latestAppraisal = appraisalOrgUnits[appraisalOrgUnits.length - 1].Appraisal;
    const appraisalUnits = appraisal.AppraisalOrgUnits;

    const address = [
      orgUnit.Address1,
      orgUnit.Address2,
      orgUnit.City,
      orgUnit.State,
      orgUnit.ZipCode
    ]
      .filter(Boolean)
      .join(', ');

    const OHTs = orgUnit.MddapOfficeOfHealthTechnologies.map(x =>
      x.OfficeOfHealthTechnology.Value.split(':')[0].replace('OHT', '')
    ).join(', ');
    const enrollmentDate = toHumanReadableDate(orgUnit.EnrollmentDate);
    const sponsorName = latestAppraisal.AppraisalTeamMembers.find(x => x.Type === APPRAISAL_SPONSOR)
      .Individual.Name;
    const teamLeadName = appraisal.AppraisalTeamMembers.find(x => x.Type === APPRAISAL_TEAM_LEAD)
      .Individual.Name;
    const appraisalDate = toHumanReadableDate(appraisal.ConductAppraisalEndDate);

    return (
      <UnitDetailsBox
        data={{
          location: address,
          FEI: orgUnit.Fei,
          enrollmentDate: enrollmentDate,
          OHTs: OHTs,
          sponsorName: sponsorName,
          appraisalDate: appraisalDate,
          appraisalID: appraisal.Id,
          teamLeadName: teamLeadName,
          appraisalUnits: appraisalUnits
        }}
        rationaleDocument={selectedAppraisalOrgUnit.ReappraisalRationaleDocument}
        handleSelectUnit={handleSelectUnit}
      />
    );
  };

  return (
    <div className='detailed-appraisal' onClick={() => setSelectedPracticeData(null)}>
      <PageInstructions
        title='Detailed Appraisal Report'
        description={description}
        fdaShareWarning={sharedWithFdaDisclaimerFlag}
      />
      {appraisalOrgUnits && !appraisalOrgUnits.length ? (
        <div className='actual-report__graph'>
          <NotEnoughDataMessage />
        </div>
      ) : (
        <div className='report__body detailed-appraisal-report'>
          <div className='report-info-panels info-box-sticky-top'>
            <ReportInfoPanel
              panelName='report-info-panel__large'
              skeletonClassName='appraisal-details-skeleton'
              loading={loading || !appraisalOrgUnits}
              renderChildren={renderAppraisalInfoPanelContent}
            />
            <ReportInfoPanel panelName='report-info-panel__more-info report-info-panel__small'>
              <SelectedPracticeInformation selectedPracticeData={selectedPracticeData} />
            </ReportInfoPanel>
          </div>
          <div className='report-wrapper'>
            <div className='program-year-legend-block'>
              <div>
                <p className='bold'>Program Year</p>
                <ProgramYearFilter
                  additionalClassNames='program-year-dropdown'
                  handleOnChange={setSelectedAppraisalId}
                  programYearOptions={programYearOptions || []}
                  selectedAppraisalId={selectedAppraisalId}
                  loading={loading}
                />
              </div>
              <CharacterizationLegend />
            </div>
            <DetailedReportTable
              appraisalOrgUnit={selectedAppraisalOrgUnit}
              isPercentageVisible={isPercentageVisible}
              practiceAreas={practiceAreas}
              selectedPracticeId={selectedPracticeData?.practice.id}
              handlePracticeSelected={setSelectedPracticeData}
              loading={loading || !appraisalOrgUnits}
            />
          </div>
        </div>
      )}
    </div>
  );
};

export default DetailedAppraisalReport;
